import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Client } from '../../../models/client';
import Title from '../../../components/Title';
import { NavLink } from 'react-router-dom';
import { Add as AddIcon, Info as InfoIcon, Save as SaveIcon } from '@mui/icons-material';
import { useTheme, useMediaQuery, Tooltip, Typography, Button, Box, Card, Grid, FormControl, MenuItem, InputLabel, Select, CardContent, Divider } from '@mui/material';
import { useSelection } from '../../../components/context/SelectionContext';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import SortableItem from './sortItem'; // Componente para cada fila
import { Collect } from '../../../models/collect';
import { Office } from '../../../models/office';
import { Lend } from '../../../models/lend';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import HeaderResponsive from '../../../components/layouts/HeaderResponsive';
import PermissionButton from '../../../components/common/PermissionButton';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';

interface ShowAlerts {
    showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
    showLoading: () => void;
    hideLoading: () => void;
}

const DoRoute: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
    document.title = "Administrar Clientes";
    const { canUpdate } = useResourcePermissions('/procesos/rutero');
    const [clients, setClients] = useState<Lend[]>([]);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [collectsOffice, setCollectsOffice] = useState([]);
    const [offices, setOffices] = useState([]);
    const { office_id, setOffice_id, collect_id, setCollect_id, user_id, setUser_id } = useSelection();
    const [showModal, setShowModal] = useState(false);
    const [alert, setAlert] = useState(null);
    const [open, setOpen] = useState(false); // Estado para controlar la apertura del modal
    const [modalConfirm, setModalConfirm] = useState(false); // Estado para confirmar la acción

    // Cargar oficinas y datos relacionados con office_id
    useEffect(() => {
        const fetchData = async () => {
            showLoading();
            try {
                const officesResponse = axios.get('offices');
                const collectsOfficeResponse = office_id ? axios.get(`collectsoffice/${office_id}`) : null;

                const [officesData, collectsOfficeData] = await Promise.all([
                    officesResponse,
                    collectsOfficeResponse,
                ]);

                if (officesData) setOffices(officesData.data.data);
                if (collectsOfficeData) setCollectsOffice(collectsOfficeData.data);
                setClients([]);
                await new Promise((resolve) => setTimeout(resolve, 1000));
            } catch (error) {
                //console.log('Error fetching data:', 'error');
            } finally {
                hideLoading();
            }
        };

        fetchData();
    }, [office_id]);

    // Cargar clientes solo cuando collects tenga un valor válido
    useEffect(() => {
        const ClientesCollect = async () => {
            if (!collect_id) {
                showAlert('No existen cobros asociados a la oficina!', 'info');
                return;
            }
            showLoading();
            try {
                const { data } = await axios.get(`lendcollectclient/${collect_id}`);
                setClients(data.data);
                await new Promise((resolve) => setTimeout(resolve, 1000)); // Simular un retraso
                showAlert('Se ha realizado la consulta exitosamente!', 'info');
            } catch (error) {
                showAlert('Error fetching data!', 'error');
            } finally {
                hideLoading();
            }
        };

        ClientesCollect();
    }, [collect_id]);

    // Configuración de sensores para el drag and drop
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Manejar el evento de arrastrar y soltar
    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (active.id !== over?.id) {
            setClients((clients) => {
                const oldIndex = clients.findIndex((client) => client.id === active.id);
                const newIndex = clients.findIndex((client) => client.id === over?.id);
                return arrayMove(clients, oldIndex, newIndex);
            });
        }
    };

    // Función para abrir el modal de confirmación
    const handleOpenModal = () => {
        if (!canUpdate) {
            showAlert('Sin permiso para guardar cambios de ruta.', 'warning');
            return;
        }
        setOpen(true);
    };

    // Función para cerrar el modal
    const handleCloseModal = () => {
        setOpen(false);
    };

    // Función para confirmar y enviar la información
    const handleConfirm = async () => {
        setOpen(false); 
        await submit();
    };

    // Función para enviar la información a la API
    const submit = async () => {
        const formData = clients.map((client, index) => ({
            lend_id: client.id,
            position: index + 1,
        }));
        try {
            showLoading();
            const response = await axios.post(`routeposition`, { parsedNewPositionRoute: formData });
            showAlert('Posición de la ruta actualizada correctamente!', 'success');
            await new Promise(response => setTimeout(response, 2000));
        } catch (error) {
            showAlert('Error al actualizar la posición de la ruta!', 'error');
        } finally {
            hideLoading();
        }
    };

    return (
        <HeaderResponsive>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Title>Cambio de Posición Préstamos</Title>
                <PermissionButton
                    allowed={canUpdate}
                    deniedTooltip="Sin permiso para guardar"
                    tooltip="Guardar"
                    variant="contained"
                    startIcon={isMobile ? null : <SaveIcon />}
                    onClick={handleOpenModal}
                >
                    {isMobile ? <SaveIcon /> : "Guardar"}
                </PermissionButton>
            </Box>
            <Card>
                <form onSubmit={(e) => e.preventDefault()}>
                    <CardContent>
                         <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} xl={6}>
                                <FormControl fullWidth>
                                    <InputLabel id="form-layouts-separator-select-label">
                                        Seleccionar Oficina
                                    </InputLabel>
                                    {offices && offices.length > 0 ? (
                                        <Select
                                            label="Seleccionar Oficina"
                                            value={office_id}
                                            id="form-layouts-separator-select"
                                            labelId="form-layouts-separator-select-label"
                                            onChange={(event) => setOffice_id(event.target.value)}
                                            required
                                        >
                                            {offices.map((office: Office) => (
                                                <MenuItem key={office.id} value={office.id}>
                                                    {office.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    ) : null}
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} xl={6}>
                                <FormControl fullWidth>
                                    <InputLabel id="collect">Seleccionar Cobro</InputLabel>
                                    {collectsOffice && collectsOffice.length > 0 ? (
                                        <Select
                                            label="Seleccionar Cobro"
                                            value={collect_id}
                                            id="collects_id"
                                            labelId="collects_id"
                                            onChange={(event) => setCollect_id(event.target.value)}
                                            required
                                        >
                                            {collectsOffice.map((collect: Collect) => (
                                                <MenuItem key={collect.id} value={collect.id}>
                                                    {collect.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    ) : null}
                                </FormControl>
                            </Grid>
                        </Grid>
                    </CardContent>
                </form>
                <CardContent>
                    <Divider sx={{ margin: 2 }} />
                    {clients.length > 0 ? (
                        <div style={{ height: '100%', width: '100%', alignItems: 'center' }}>
                            <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragEnd={handleDragEnd}
                                modifiers={[restrictToVerticalAxis]}
                            >
                                <SortableContext items={clients} strategy={verticalListSortingStrategy}>
                                    {clients.map((lend, index) => (
                                        <SortableItem key={lend.id} id={lend.id}>
                                            <Box display="flex" alignItems="center">
                                                <Typography sx={{ marginRight: 2 }}>{index + 1}. </Typography> {/* Índice */}
                                                <Typography>{lend.client.idnumber} - {lend.client.fullname}</Typography> {/* Datos del cliente */}
                                            </Box>
                                        </SortableItem>
                                    ))}
                                </SortableContext>
                            </DndContext>
                        </div>
                    ) : (
                        <Box p={3} textAlign="center">
                            <Box display="flex" alignItems="center" justifyContent="center">
                                <InfoIcon color="info" style={{ marginRight: 8 }} />
                                <Typography variant="h6">No existen registros</Typography>
                            </Box>
                        </Box>
                    )}
                </CardContent>
            </Card>

            {/* Modal de confirmación */}
            <Dialog open={open} onClose={handleCloseModal}>
                <DialogTitle>Confirmación</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        ¿Estás seguro de que deseas actualizar la posición de la ruta?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseModal} color="primary">Cancelar</Button>
                    <Button onClick={handleConfirm} color="secondary">Confirmar</Button>
                </DialogActions>
            </Dialog>
        </HeaderResponsive>
    );
};

export default DoRoute;
